<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useTranslate } from '@/composables/language'
import { $bus, mqtt } from '@/common/events'
import { defaultServiceOptions } from '@/common/config'
import { dialog } from '@/common/dialog'
import { nanoid } from '@/common/helper'
import { hcu } from '@/common/hcu'
import { useTitle } from '@/composables/title'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import { useAppState } from '@/composables/app'
import { useAuthState } from '@/composables/auth'
import { useVirtualKeyboard } from '@/composables/keyboard'
import { useCabinetControl } from '@/composables/cabinet'
import { useSessionTimer } from '@/composables/session'
import { useCabinetAlarm } from '@/composables/alarm'
import { useSettings } from '@/composables/settings'
import { canInterrupt } from '@/composables/interrupt'
import { CabinetService } from '@/services/CabinetService'
import { LogService } from '@/services/LogService.js'
import Label from '@/components/Label.vue'
import TextBox from '@/components/TextBox.vue'
import Button from '@/components/Button.vue'
import EventLog from '@/components/EventLog.vue'
import DropDown from '@/components/DropDown.vue'
import DrumPosition from '@/components/DrumPosition.vue'
import CabinetB414 from '@/views/service/components/CabinetB414.vue'
import CabinetLocker from '@/views/service/components/CabinetLocker.vue'
import CabinetM100 from '@/views/service/components/CabinetM100.vue'
import CabinetM280 from '@/views/service/components/CabinetM280.vue'
import CabinetM280T from '@/views/service/components/CabinetM280T.vue'
import BarcodeReader from '@/views/service/components/BarcodeReader.vue'
import SmartCardReader from '@/views/service/components/SmartCardReader.vue'
import WeightScale from '@/views/service/components/WeightScale.vue'

let cabinetService = new CabinetService();
let logService = null;

const { t } = useTranslate();
const authStore = useAuthState();

const isLoading = ref(true);
const { isKeyboardVisible } = useVirtualKeyboard();

const { alarmBus } = useCabinetAlarm();
const { appState } = useAppState();

const cabinets = ref([
  // { id: "CAfvi3BLoCw...", name: "B414/1", master: true, model: "plt-b414" cabinet: { ... } },
]);

const controllableDevices = ref([
]);

const masterCabinetId = ref();

const selectedCabinetId = ref(null);

const selectedCabinet = computed(() => {
  return controllableDevices.value && controllableDevices.value.length > 0 ? controllableDevices.value.find(x => x.id == selectedCabinetId.value) : null;
});

const canSelectCabinet = computed(() => {
  return controllableDevices.value && controllableDevices.value.length > 1 ? true : false;
});

const currentUser = ref();

const error = ref(null);

onMounted(async () => {
  useTitle(t('service.title'));
  useMenu("calibration");
  useSubmenu(false);
  useTopBar(true);

  canInterrupt(false);
  useSessionTimer(false);

  logService = new LogService(defaultServiceOptions());

  try {
    await loadCabinets();
  }
  catch (err) {
    console.error("[service] loadCabinets error", err);
    if (err.message.startsWith("JSON.parse")) {
      error.value = new Error(t('service.hcu_not_responding'));
    }
    else {
      error.value = err;
    }
  }

  try {
    let permissions = authStore.permissions.value || [];
    //isAdmin.value = permissions.includes("cabinet.admin");
    currentUser.value = authStore.user.value;
    let userId = authStore.userId.value;
    if (appState.isLogEnabled) {
      await logService.insertLog({
        a: "cabinet.serviceWindow",
        t: "cabinet",
        s: "warning",
        uid: userId,
        cid: masterCabinetId.value,
        msg: "Servisno okno: dostop do ročnega upravljanja omare",
        ts: new Date()
      });
    }
  }
  catch (err) {
  }

  isLoading.value = false;
});

onUnmounted(() => {
  canInterrupt(true);
});

async function onLog(record) {
  try {
    if (!record) {
      return;
    }
    if (!record.a) {
      record.a = "cabinet.serviceAction"
    }
    if (!record.t) {
      record.t = "cabinet";
    }
    if (!record.s) {
      record.s = "warning";
    }
    if (!record.uid) {
      let userId = authStore.userId.value;
      record.uid = userId;
    }
    if (!record.ts) {
      record.ts = new Date();
    }

    //Check if logging is enabled
    if (!appState.isLogEnabled) {
      return;
    }

    //Add to history log
    await logService.insertLog(record);
  }
  catch (err) {
  }
}

async function loadCabinets() {
  let result = await hcu.cabinet().getInstances2();

  //Check if any cabinet defined
  if (!result.controllers || !(result.controllers instanceof Array) || result.controllers.length == 0) {
    throw new Error(t('service.no_cabinet_defined'));
  }

  let list = [ ];

  for (let cabinet of result.controllers) {
    let item = {
      id: cabinet.id,
      name: cabinet.name || cabinet.model?.toUpperCase() || "???",
      mac: cabinet.mac,
      model: cabinet.model,
      master: !!cabinet.master,
      cabinet: cabinet
    };
    if (cabinet.master) {
      masterCabinetId.value = cabinet.id;
    }
    list.push(item);
  }


  let barcode = {
    id: "barcode",
    name: "Čitalec črtnih kod",
    //mac: device.mac,
    type: "barcode",
    model: "generic",
  };
  list.push(barcode);

  let rfid = {
    id: "rfid",
    name: "Čitalec kartic",
    //mac: device.mac,
    type: "rfid",
    model: "wiegand",
  };
  list.push(rfid);

  let scale = {
    id: "scale",
    name: "Tehtnica",
    //mac: device.mac,
    type: "scale",
    model: "plt-ws2",
  };
  list.push(scale);

  

  cabinets.value = list;
  controllableDevices.value = list.filter(x => x.model != "none" && x.model != "combined");

  //Automatically select the master or the first one
  if (!selectedCabinet.value && controllableDevices.value.length > 0) {
    let first = controllableDevices.value.find(x => x.master) || controllableDevices.value[0];
    selectedCabinetId.value = first.id;
  }
}

const selectedCabinet2 = ref(null);

watch(selectedCabinetId, async () => {
  let c = selectedCabinet.value;
  if (c) {
    try {
      let { cabinet } = controllableDevices.value.find(x => x.id == c.id);

      if (cabinet?.mac) {
        let status = await hcu.cabinet(cabinet.mac).getStatus2();
        let keep = "id,mac,master,model,name".split(/,/g);
        Object.keys(status).forEach(key => {
          if (!keep.includes(key)) {
            cabinet[key] = status[key]; //Override specific fields
          }
        });
      }
    }
    catch (err) {
      console.error("selectedCabinetId", err);
      if (err.message.startsWith("JSON.parse")) {
        error.value = new Error(t('service.hcu_not_responding'));
      }
      else {
        error.value = err;
      }
    }
  }
});

function isSelected(item) {
  //return item.id == selectedCabinetId; //Any, but may return more than one item if id is the same
  let selected = controllableDevices.value?.find(x => x.id == selectedCabinetId.value); //First, only one item
  return item == selected;
}
</script>

<template>
  <div class="relative screen-container">

    <!-- Loading animation -->
    <div class="grid place-items-center w-full h-[calc(100vh-120px)] " v-if="isLoading && !error">
      <Spinner size="12"></Spinner>
    </div>

    <!-- Header -->
    <div class="flex flex-row justify-between space-x-2" v-if="!isLoading">
      <h2 class="text-lg font-bold dark:text-white">{{ $t('service.serviceWindow') }}</h2>
      <DropDown :enabled="canSelectCabinet" :options="controllableDevices" v-model="selectedCabinetId" :placeholder="$t('service.select_cabinet')" style="width: 175px;"></DropDown>
    </div>

    <!-- Error message -->
    <div v-if="error">
      <pre class="message-error"><i class="fa fa-xmark-circle mr-4"></i>{{ error.message }}</pre>
    </div>

    <!-- Control board -->
    <div v-if="!isLoading && !error && controllableDevices?.length">
      <template v-for="item of controllableDevices">
        <template v-if="isSelected(item)">
          <CabinetB414 :cabinet="item.cabinet" :user="currentUser" v-if="item.model == 'plt-b414'" @onLog="onLog" />
          <CabinetLocker :cabinet="item.cabinet" :user="currentUser" v-else-if="item.model == 'plt-l18'" @onLog="onLog" />
          <CabinetLocker :cabinet="item.cabinet" :user="currentUser" v-else-if="item.model == 'plt-l64'" @onLog="onLog" />
          <CabinetM100 :cabinet="item.cabinet" :user="currentUser" v-else-if="item.model == 'plt-m100'" @onLog="onLog" />
          <CabinetM280 :cabinet="item.cabinet" :user="currentUser" v-else-if="item.model == 'plt-m280'" @onLog="onLog" />
          <CabinetM280T :cabinet="item.cabinet" :user="currentUser" v-else-if="item.model == 'plt-m280t'" @onLog="onLog" />
          <BarcodeReader :device="item" :user="currentUser" v-else-if="item.type == 'barcode'" @onLog="onLog" />
          <SmartCardReader :device="item" :user="currentUser" v-else-if="item.type == 'rfid'" @onLog="onLog" />
          <WeightScale :device="item" :user="currentUser" v-else-if="item.type == 'scale'" @onLog="onLog" />
          <div v-else-if="item.model == 'none' || item.model == 'combined'">
            <pre class="p-5 mt-4 mb-2 border rounded bg-red-100 text-red-700 text-sm">{{ $t('service.controller_is_abstract') }}</pre>
          </div>
          <div v-else>
            <pre class="p-5 mt-4 mb-2 border rounded bg-red-100 text-red-700 text-sm">{{ $t('service.controller_not_supported') }}</pre>
            <pre class="px-3 py-3 mt-8 mb-10 border rounded bg-amber-100 text-amber-700 text-xs font-mono">{{ item }}</pre>
          </div>
        </template>
      </template>
    </div>

    <div class="distancer keyboard-height" v-if="isKeyboardVisible"></div>

  </div>
</template>

<style scoped>
.screen-container {
  @apply block w-full p-5 dark:bg-gray-900 dark:text-white;
}

.message-error {
  @apply p-5 mt-4 mb-10 font-sans border rounded-md bg-red-100 text-red-700 text-sm dark:bg-red-800 dark:border-red-900 dark:text-white;
}
</style>
